import React from "react";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState, useLayoutEffect, useCallback } from "react";
import ApiData from "../../../api/ApiData";
import User from "../../../api/User";
import ReportData from "../../../api/ReportData";
import { ListSerialisationReport } from "./ListSerialisationReport";
import { ContainerSubTitleGrey } from "../../ContainerSubTitleGrey";
import Msg from "../../../api/Msg";
import { CalendarV1 } from "../../UI/date/CalendarV1";
import { ExportToExcel } from "../../../Export/ExportXLSX";
import Media from "../../../api/Media";
import { CcV4Toggle } from "../../UI/CcV4Toggle";
import AlertData from "../../../api/AlertData";
import stateUtils from "../../../utils/state.utils";
import { BtnPrimaryv4 } from "../../UI/button/BtnPrimaryv4";
import { BtnOutlineV4 } from "../../UI/button/BtnOutlineV4";
import Loader from "../../Loader";
import { ReportsManagerFilterBar } from "../ReportsManagerFilterBar";
import {
  filterRowsByInn,
  saveReportsShowMineOnly,
  useReportsShowMineOnly,
} from "../../../utils/reportManagerFilterUtils";
import { useApplyReportTemplate } from "../../../hooks/useApplyReportTemplate";
import { SaveReportTemplateButton } from "../SaveReportTemplateButton";
import ReportExportActionsColumn from "../ReportExportActionsColumn";
import { REPORT_TAB_SERIALISATION } from "../../../utils/reportTemplatesStorage";
import {
  SERIAL_METRIC_FIELDS,
  buildSerialisationExcelRows,
  readSerialColumnVisibility,
  saveSerialColumnVisibility,
} from "../../../utils/serialisationReportUtils";

const COLUMN_TOGGLE_LABELS = {
  verify: "Верификация",
  incident: "Инциденты",
  defect: "Брак",
  print: "Напечатано",
};

function initialSerialisationEndDate() {
  const raw = AlertData.from_defect_report_date;
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    return stateUtils.dateYYYYMMDD(raw);
  }
  if (typeof raw === "string" && raw.length >= 10) {
    return stateUtils.dateYYYYMMDD(raw);
  }
  const fallback = new Date();
  fallback.setDate(fallback.getDate() - 14);
  return stateUtils.dateYYYYMMDD(fallback);
}

export const SerialisatonReport = observer(() => {
  const [begin, setBegin] = useState("2025-09-10");
  const [end, setEnd] = useState(initialSerialisationEndDate);
  const [hiddenZiro, setHiddenZiro] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [search, setSearch] = useState("");
  const [showMineOnly, setShowMineOnly] = useReportsShowMineOnly();
  const [columnVisibility, setColumnVisibility] = useState(() =>
    readSerialColumnVisibility(),
  );

  const calendarYear = new Date().getFullYear();

  const loadReport = useCallback(
    async (beginDate, endDate, hideZeroVerification = hiddenZiro) => {
      if (!beginDate) {
        return Msg.writeMessages("Не выбрана дата начала отчёта");
      }
      if (!endDate) {
        return Msg.writeMessages("Не выбрана дата окончания отчёта");
      }
      setFetching(true);
      try {
        if (ApiData.companys === null) {
          const res = await ApiData.getDataCompanys();
          if (res) {
            await ReportData.reportEventTotalsSa(
              ApiData.companys,
              beginDate,
              endDate,
            );
          }
        } else {
          await ReportData.reportEventTotalsSa(
            ApiData.companys,
            beginDate,
            endDate,
          );
        }
        ReportData.visibleAllList(hideZeroVerification);
      } finally {
        setFetching(false);
      }
    },
    [hiddenZiro],
  );

  const getListCompany = () => loadReport(begin, end);

  const applyTemplateFields = useCallback(
    async (fields) => {
      const beginDate = fields.begin ?? begin;
      const endDate = fields.end ?? end;
      setBegin(beginDate);
      setEnd(endDate);
      setHiddenZiro(fields.hiddenZero !== false);
      saveReportsShowMineOnly(Boolean(fields.showMineOnly));
      setShowMineOnly(Boolean(fields.showMineOnly));
      setSearch(fields.search ?? "");
      await loadReport(
        beginDate,
        endDate,
        fields.hiddenZero !== false,
      );
    },
    [begin, end, loadReport, setShowMineOnly],
  );

  useApplyReportTemplate(REPORT_TAB_SERIALISATION, applyTemplateFields);

  const getFieldsSnapshot = useCallback(
    () => ({
      begin,
      end,
      hiddenZero: hiddenZiro,
      showMineOnly,
      search,
    }),
    [begin, end, hiddenZiro, search, showMineOnly],
  );

  const runCalendarYearReport = (year) => {
    const beginDate = `${year}-01-01`;
    const endDate = `${year}-12-31`;
    setBegin(beginDate);
    setEnd(endDate);
    loadReport(beginDate, endDate);
  };

  const setColumnVisible = (field, visible) => {
    setColumnVisibility((prev) => {
      const next = { ...prev, [field]: visible };
      saveSerialColumnVisibility(next);
      return next;
    });
  };

  useEffect(() => {
    ReportData.visibleAllList(hiddenZiro);
  }, [hiddenZiro]);

  const handlerPeriod = (e) => {
    if (e.target.id === "begin") {
      setBegin(e.target.value);
    }
    if (e.target.id === "end") {
      setEnd(e.target.value);
    }
  };

  useEffect(() => {
    if (ReportData.serialisation_list_original.length !== 0) {
      ReportData.setParameter("creatingCompliteSerialisationFlag", true);
      ReportData.setParameter("creatingProcessSerialisationFlag", true);
    }
  }, []);

  useLayoutEffect(() => {
    ReportData.setParameter("loadingCount", 0);
  }, []);

  const hasSerialRows =
    Array.isArray(ReportData.serialisation_list_original) &&
    ReportData.serialisation_list_original.length > 0;

  const showTable = hasSerialRows && !fetching;
  const showLoading =
    fetching ||
    (!hasSerialRows &&
      ReportData.creatingProcessSerialisationFlag &&
      !ReportData.creatingCompliteSerialisationFlag);

  const displayList = useMemo(() => {
    if (!showTable || !ReportData.serialisation_list) {
      return [];
    }
    return filterRowsByInn(ReportData.serialisation_list, {
      searchQuery: search,
      showMineOnly,
      currentUserId: User.user?.id,
      companys: ApiData.companys,
    });
  }, [
    showTable,
    ReportData.serialisation_list,
    search,
    showMineOnly,
    User.user?.id,
    ApiData.companys,
  ]);

  const excel = useMemo(() => {
    if (!showTable) {
      return [];
    }
    return buildSerialisationExcelRows(
      displayList,
      columnVisibility,
      ApiData.companys,
    );
  }, [showTable, displayList, columnVisibility, ApiData.companys]);

  return (
    <section className="card reports-tab-panel-section" aria-label="Сериализация">
      <header className="reports-panel-head">
        <h2 className="dash-card-title">Сводный отчёт событий</h2>
        <p className="reports-panel-desc">
          Верификация, инциденты, брак и печать по организациям за выбранный
          период
        </p>
      </header>

      <div className="reports-toolbar-stack">
        <div className="reports-toolbar reports-toolbar--serial reports-toolbar--serial-primary">
          <ReportsManagerFilterBar
            search={search}
            onSearchChange={(e) => setSearch(e.target.value)}
            showMineOnly={showMineOnly}
            onShowMineOnlyChange={setShowMineOnly}
          />
          <div className="reports-date-range">
            <span className="reports-date-label">
              {!Media.mobile ? "Период с" : "С"}
            </span>
            <CalendarV1 id="begin" value={begin} onChange={handlerPeriod} />
            <span className="reports-date-label">по</span>
            <CalendarV1 id="end" value={end} onChange={handlerPeriod} />
          </div>
          <BtnPrimaryv4
            text={!Media.mobile ? "Отчёт за период" : "За период"}
            onClick={getListCompany}
          />
          <div className="reports-serial-year-buttons">
            <BtnOutlineV4
              text={`Отчёт ${calendarYear}`}
              onClick={() => runCalendarYearReport(calendarYear)}
            />
            <BtnOutlineV4
              text={`Отчёт ${calendarYear - 1}`}
              onClick={() => runCalendarYearReport(calendarYear - 1)}
            />
          </div>
          <CcV4Toggle
            label="Скрыть нулевую верификацию"
            active={hiddenZiro}
            onClick={() => setHiddenZiro(!hiddenZiro)}
          />
          <ReportExportActionsColumn>
            <SaveReportTemplateButton
              tab={REPORT_TAB_SERIALISATION}
              getFieldsSnapshot={getFieldsSnapshot}
            />
            <ExportToExcel
              fileName="Отчёт событий по всем компаниям"
              excel={excel}
            />
          </ReportExportActionsColumn>
        </div>
        <div
          className="reports-toolbar reports-toolbar--serial reports-toolbar--serial-cols"
          aria-label="Видимость колонок отчёта"
        >
          {SERIAL_METRIC_FIELDS.map((field) => (
            <CcV4Toggle
              key={field}
              label={COLUMN_TOGGLE_LABELS[field]}
              active={columnVisibility[field]}
              onClick={() =>
                setColumnVisible(field, !columnVisibility[field])
              }
            />
          ))}
        </div>
      </div>

      {showLoading ? (
        <div className="reports-loading-block">
          <ContainerSubTitleGrey text="Строим отчёт, пожалуйста, подождите..." />
          <Loader />
        </div>
      ) : showTable ? (
        <ListSerialisationReport
          columnVisibility={columnVisibility}
          rows={displayList}
          companys={ApiData.companys}
        />
      ) : (
        <ContainerSubTitleGrey text="Для просмотра отчёта настройте фильтр" />
      )}
    </section>
  );
});
